<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';

import Badge from 'lib/components/badge/Badge.vue';
import {
  TreeNode,
  TreeSelectionKeys,
} from 'lib/components/basetree/BaseTree.vue.d';
import Button from 'lib/components/button/Button.vue';
import {
  ButtonSelectTreeEmits,
  ButtonSelectTreeProps,
  NodeModelValue,
} from 'lib/components/buttonselecttree/ButtonSelectTree.vue.d';
import DialogSelectTree from 'lib/components/dialogselecttree/DialogSelectTree.vue';
import DialogSelectTreeInstance from 'lib/components/dialogselecttree/DialogSelectTree.vue.d';
import FieldWrapper from 'lib/components/fieldwrapper/FieldWrapper.vue';
import Icon from 'lib/components/icon/Icon.vue';
import ValidatorMessage from 'lib/components/validatormessage/ValidatorMessage.vue';

import { FieldValidation } from '../form/Form.vue.d';

const props = withDefaults(defineProps<ButtonSelectTreeProps>(), {
  fieldLabel: undefined,
  selectionMode: 'checkbox',
  fieldName: 'selectTree',
  label: undefined,
  validatorMessage: undefined,
  fieldInfo: undefined,
  mandatory: false,
  invalid: false,
  disableNodeAll: false,
  dialogHeader: undefined,
  disableNodeWhen: undefined,
  allowEmptySelection: true,
  fetchTreeOnButtonRender: false,
  singleEditable: true,
});

const emit = defineEmits<ButtonSelectTreeEmits>();

onMounted(() => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName, (value: number[]) => {
        return setValidatorMessage(value);
      }),
    );

    if (props.value) {
      dialogTreeInstance.value?.treeComponent?.fetchTreeList();
      field.value = props.value;
    }
  }
});

const field = reactive<FieldValidation<NodeModelValue>>({
  value: props.value,
});

const dialogTreeInstance = ref<DialogSelectTreeInstance>();
const showDialogTree = ref<boolean>(props.fetchTreeOnButtonRender);
const hideDialogTreeOnRender = ref<boolean>(props.fetchTreeOnButtonRender);
const dialogTreeKey = ref<number>(0);

/**
 * The node selected on single mode.
 */
const selectedSingleTreeNode = ref<TreeNode | undefined>(
  !Array.isArray(props.value) &&
    typeof props.value !== 'number' &&
    typeof props.value !== 'string'
    ? props.value
    : undefined,
);

const singleTreeNodeModel = computed<TreeNode | undefined>({
  get: () => selectedSingleTreeNode.value,
  set: (node: TreeNode | undefined) => {
    selectedSingleTreeNode.value = node;
    emit('update:value', getValue(node, props.fieldDataKey));
  },
});

const treeKeys = computed(() => {
  return collectKeyValue(field.value ?? props.value);
});

const buttonLabel = computed(() => {
  const keysExcludeAll =
    props.selectionMode === 'checkbox'
      ? (treeKeys.value?.filter((key) => key?.toString() != '-1') ?? [])
      : [];

  const keyCount = keysExcludeAll.length;

  if (keyCount && props.selectionMode === 'checkbox') {
    return (
      keyCount +
      (props.type === 'group' ? ' group(s) selected' : ' category(s) selected')
    );
  }

  if (props.label) return props.label;

  return props.type === 'group' ? 'Select Group' : 'Select Category';
});

const bindBtnWidth = computed(() =>
  props.width ? `width: ${props.width}px !important` : undefined,
);

const collectKeyValue = (source?: NodeModelValue): number[] => {
  // Assuming session storage for flatnodes is provided by fetching tree from sidebar
  const flatNodes = JSON.parse(
    sessionStorage.getItem(`flatten${props.type}nodes`) ?? '[]',
  );

  if (source) {
    return (Array.isArray(source) ? source : [source]).map((each) => {
      if (typeof each === 'number') {
        return each;
      } else if (typeof each === 'string') {
        const matchedNode = flatNodes.find((node) => {
          return node._id === each;
        });

        return matchedNode?.key;
      }
      return each.key;
    });
  }

  return [];
};

const getValue = (
  source?: NodeModelValue,
  dataKey?: string,
): NodeModelValue | undefined => {
  if (Array.isArray(source) && props.selectionMode === 'checkbox') {
    return source.map((each) => {
      return dataKey ? each?.[dataKey] : each;
    });
  } else if (source && props.selectionMode === 'single') {
    return dataKey ? source?.[dataKey] : source;
  }

  return undefined;
};

const setValidatorMessage = async (
  value: number[],
): Promise<boolean | string> => {
  await nextTick();

  if (
    ((Array.isArray(value) && !value?.length) ||
      (!Array.isArray(value) && !value)) &&
    props.mandatory
  ) {
    if (props.validatorMessage) return props.validatorMessage;

    return props.type === 'group'
      ? 'You must pick a group'
      : 'You must pick a category';
  } else if (props.invalid && props.validatorMessage) {
    return props.validatorMessage;
  }

  return true;
};

const setTreeSelection = (event: TreeSelectionKeys | undefined): void => {
  if (event) {
    const { keys, singleNode, selectedNodes } = event as {
      keys: TreeSelectionKeys;
      selectedNodes: TreeNode[];
      singleNode: TreeNode;
    };

    // Let keysModel: NodeModelValue = undefined;
    const isAllSelected = keys[-1]?.checked ?? false;

    if (props.selectionMode === 'single') {
      singleTreeNodeModel.value = singleNode;
      // If (singleNode?.key) keysModel = [Number(singleNode.key)];

      field.value = getValue(singleTreeNodeModel.value, props.fieldDataKey);
    } else {
      // KeysModel = parseNodeKeys(keys);

      const filterredExcludedNodes = selectedNodes?.filter((each) => {
        return !props.excludedKeys?.includes(each.key as string);
      });

      field.value = getValue(filterredExcludedNodes, props.fieldDataKey) ?? [];
    }

    /*
     * Const filterredExcludedKeys = keysModel?.filter((each) => {
     *   return !props.excludedKeys?.includes(each);
     * });
     */

    // Emit('update:keys', filterredExcludedKeys, isAllSelected);
    emit('update:value', field.value, isAllSelected);
  }
};

const resetSingleGroupNode = (): void => {
  singleTreeNodeModel.value = undefined;
  dialogTreeKey.value++;
  emit('reset');
};

const showDialogSelectTree = (): void => {
  showDialogTree.value = true;
  hideDialogTreeOnRender.value = false;
  emit('dialogShown');
};

watch(
  () => props.value,
  (value: NodeModelValue) => {
    field.value = value;
  },
);

watch(
  () => props.selectedNode,
  (node: TreeNode) => {
    if (props.selectionMode === 'single') selectedSingleTreeNode.value = node;
  },
  { immediate: true },
);

defineExpose({ dialogTreeKey });
</script>

<template>
  <FieldWrapper
    v-bind="$attrs"
    :info="fieldInfo"
    :label="props.fieldLabel"
    :mandatory="props.mandatory"
    :show-optional-text="$attrs['show-optional-text'] != false"
  >
    <span v-if="selectedSingleTreeNode" class="inline-flex gap-1 items-center">
      <Badge :label="selectedSingleTreeNode.name" format="nowrap" />

      <Button
        v-if="singleEditable"
        @click="showDialogSelectTree"
        class="p-0 m-0 !min-w-0 !min-h-0 !h-4 !w-4 focus-visible:ring-0 focus-visible:bg-primary-50"
        text
      >
        <Icon class="w-full h-full" icon="edit" severity="primary" />
      </Button>

      <Button
        v-if="singleResetable"
        @click="resetSingleGroupNode"
        class="p-0 m-0 !min-w-0 !min-h-0 !h-4 !w-4 focus-visible:ring-0 focus-visible:bg-danger-50"
        text
      >
        <Icon class="w-full h-full" icon="close" severity="danger" />
      </Button>
    </span>

    <Button
      v-else
      :class="{ '!w-full': !props.width }"
      :disabled="disabled"
      :label="buttonLabel"
      :style="bindBtnWidth"
      @click="showDialogSelectTree"
    />

    <ValidatorMessage :message="field.errorMessage" />
  </FieldWrapper>

  <DialogSelectTree
    :key="dialogTreeKey + +hideDialogTreeOnRender"
    ref="dialogTreeInstance"
    v-model:visible="showDialogTree"
    :allow-empty-selection="allowEmptySelection"
    :auto-select-all="autoSelectAll"
    :disable-keys="disableKeys"
    :disable-node-all="disableNodeAll"
    :disable-node-when="disableNodeWhen"
    :exact-disable-key="exactDisableKey"
    :exact-disable-keys="exactDisableKeys"
    :excluded-keys="excludedKeys"
    :fetch-tree="fetchTree"
    :flatten-disposable-node="flattenDisposableNode"
    :header="dialogHeader"
    :hidden="hideDialogTreeOnRender"
    :keys="treeKeys"
    :params="params"
    :readonly="props.readonly"
    :selection-mode="props.selectionMode"
    :show-disposable-groups="props.showDisposableGroups"
    :type="props.type"
    @hide="$emit('dialogHidden')"
    @select="setTreeSelection"
  >
    <template #treenode="{ node }" v-if="$slots.treenode">
      <slot :node="node" name="treenode" />
    </template>
  </DialogSelectTree>
</template>
